import { Icon } from '@design/components/icon/Icon';
import {
  isSvgIconName,
  type SvgIconNames,
} from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, { HTMLAttributes, ReactNode, Ref } from 'react';
import { z } from 'zod';

import styles from './Badge.module.scss';

export const BADGE_SIZES = ['xsmall', 'small', 'medium'] as const;
export const BADGE_STYLES = ['solid', 'outline'] as const;
export const BADGE_VARIANTS = ['textual', 'numerical'] as const;
export const BADGE_COLORS = [
  'neutral',
  'primary',
  'error',
  'success',
  'warning',
] as const;

export const badgeIconNameSchema = z.custom<SvgIconNames>(
  value => typeof value === 'string' && isSvgIconName(value),
  { message: 'Expected a design-system icon name' }
);

export const badgeSizeSchema = z.enum(BADGE_SIZES);
export const badgeStyleSchema = z.enum(BADGE_STYLES);
export const badgeVariantSchema = z.enum(BADGE_VARIANTS);
export const badgeColorSchema = z.enum(BADGE_COLORS);

export const badgePropsSchema = z
  .object({
    label: z.string().min(1),
    variant: badgeVariantSchema.optional(),
    size: badgeSizeSchema.optional(),
    color: badgeColorSchema.optional(),
    badgeStyle: badgeStyleSchema.optional(),
    leftIcon: badgeIconNameSchema.optional(),
    rightIcon: badgeIconNameSchema.optional(),
  })
  .strict();

export type BadgeDataProps = z.infer<typeof badgePropsSchema>;
export type BadgeSize = z.infer<typeof badgeSizeSchema>;
export type BadgeStyle = z.infer<typeof badgeStyleSchema>;
export type BadgeVariant = z.infer<typeof badgeVariantSchema>;
export type BadgeColor = z.infer<typeof badgeColorSchema>;

export interface BadgeProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'color'>,
    Omit<Partial<BadgeDataProps>, 'label'> {
  /**
   * Layout: `'textual'` renders pill chrome with optional icons and
   * left section, `'numerical'` renders just the label as a count
   * pill (icons and `leftSection` are ignored). Defaults to `'textual'`.
   */
  variant?: BadgeVariant;
  /** Size preset. Defaults to `'medium'`. */
  size?: BadgeSize;
  /** Color treatment. Defaults to `'primary'`. Has no effect on `variant="numerical"`. */
  color?: BadgeColor;
  /** Visual style: `'solid'` (default, filled) or `'outline'` (border only). Has no effect on `variant="numerical"`. */
  badgeStyle?: BadgeStyle;
  /** Badge content. */
  label?: ReactNode;
  /** Optional icon rendered before the label. Textual variant only. */
  leftIcon?: SvgIconNames;
  /** Optional icon rendered after the label. Textual variant only. */
  rightIcon?: SvgIconNames;
  /** Arbitrary content rendered before the icon and label (e.g. an avatar). Textual variant only. */
  leftSection?: React.ReactNode;
  /** Forwarded to the outer `<div>`. */
  ref?: Ref<HTMLDivElement>;
}

const textualStyleMap: Record<BadgeStyle, Record<BadgeColor, string>> = {
  solid: {
    neutral: styles.solidNeutral,
    primary: styles.solidPrimary,
    success: styles.solidSuccess,
    warning: styles.solidWarning,
    error: styles.solidError,
  },
  outline: {
    neutral: styles.outlineNeutral,
    primary: styles.outlinePrimary,
    success: styles.outlineSuccess,
    warning: styles.outlineWarning,
    error: styles.outlineError,
  },
};

/**
 * Compact pill that surfaces status, count, or category metadata
 * alongside other content.
 *
 * Two layouts:
 *
 * - `variant="textual"` (default) renders a label with optional
 *   `leftSection`, `leftIcon`, and `rightIcon`, styled by `color`,
 *   `badgeStyle`, and `size`.
 * - `variant="numerical"` renders just the label inside a tight count
 *   pill — icons, color, and `badgeStyle` are ignored.
 */
export const Badge = ({
  color = 'primary',
  size = 'medium',
  badgeStyle = 'solid',
  variant = 'textual',
  label,
  leftIcon,
  rightIcon,
  leftSection,
  className,
  ref,
  ...rest
}: BadgeProps) => {
  const mapSizeToClass = {
    xsmall: styles.sizeXsSmall,
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
  };
  const rootClass = clsx(
    styles.root,
    size === 'small' && styles.sizeSmall,
    className
  );

  if (variant === 'numerical') {
    return (
      <div
        ref={ref}
        data-testid="badge-numerical"
        className={rootClass}
        {...rest}
      >
        <p
          data-badge="numerical"
          className={clsx(styles.numerical, mapSizeToClass[size])}
        >
          {label}
        </p>
      </div>
    );
  }

  const iconSize = size === 'small' ? 12 : 16;

  return (
    <div ref={ref} data-testid="badge-textual" className={rootClass} {...rest}>
      <div
        data-badge="textual"
        className={clsx(
          styles.textual,
          mapSizeToClass[size],
          textualStyleMap[badgeStyle][color]
        )}
      >
        {leftSection}
        {leftIcon && <Icon iconName={leftIcon} size={iconSize} />}
        <span>{label}</span>
        {rightIcon && <Icon iconName={rightIcon} size={iconSize} />}
      </div>
    </div>
  );
};
